import React from "react";

/**
 * Shared link-row renderer. Used by:
 *   - ResearchingPlaceholder (initial card before first bucket arrives)
 *   - SourceBucket (inside ResearchSourcesPanel)
 *
 * Visual baseline matches the original RenderLinks row (favicon + title with
 * the drLinkAnimate slide-in). Optional fields:
 *   - `status`     → renders the crawl-status badge (Snippet / Crawled / Extracted)
 *   - `showSnippet`→ renders the SERP snippet under the title
 *   - `showMeta`   → renders domain · source · date row
 */

const getDomain = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const StatusBadge = ({ status }) => {
  if (status === "extracted") {
    return <span className="drSrcStatus drSrcStatus--extracted" title="Crawled & extracted for chat">⚡ Extracted</span>;
  }
  if (status === "crawled") {
    return <span className="drSrcStatus drSrcStatus--crawled" title="Page body fetched">🔍 Crawled</span>;
  }
  return <span className="drSrcStatus drSrcStatus--snippet" title="Snippet only — not deep-crawled">Snippet</span>;
};

const LinkRow = ({ link, status, showSnippet, showMeta, animationDelay = 0 }) => {
  if (!link || !link.link) return null;

  const domain = getDomain(link.link);
  const favicon = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32` : "";

  return (
    <div
      className="drLinkItem drLinkAnimate"
      style={{ animationDelay: `${animationDelay}s` }}
    >
      <a
        href={link.link}
        target="_blank"
        rel="noopener noreferrer"
        className="drLinkAnchor"
      >
        {favicon ? (
          <img src={favicon} alt="" className="drLinkFaviconImg" />
        ) : (
          <span className="drLinkFavicon">?</span>
        )}
        <div className="drLinkContent">
          <div className="drLinkTitleRow">
            <span className="drLinkTitle">{link.title || domain || link.link}</span>
            {status && <StatusBadge status={status} />}
          </div>
          {showSnippet && link.snippet && (
            <p className="drLinkSnippet">{link.snippet}</p>
          )}
          {showMeta && (domain || link.source || link.date) && (
            <div className="drLinkMetaRow">
              {domain && <span className="drLinkMetaDomain">{domain}</span>}
              {link.source && <><span className="drLinkMetaSep">·</span><span>{link.source}</span></>}
              {link.date && <><span className="drLinkMetaSep">·</span><span>{link.date}</span></>}
            </div>
          )}
        </div>
      </a>
    </div>
  );
};

export default LinkRow;
